import {create} from "zustand";

import {notificationFeedService} from "../services";
import {isShiftNotificationTopic} from "../lib/notification-feed-topics";
import type {NotificationFeedResponse, NotificationItem, NotificationPageMeta} from "../types";

type NotificationFeedState = {
  items: NotificationItem[];
  unreadCount: number;
  isHydrated: boolean;
  meta: NotificationPageMeta | null;
};

type NotificationFeedActions = {
  hydrateFromApi: (response: NotificationFeedResponse) => void;
  prependFromRealtime: (item: NotificationItem) => void;
  markReadLocal: (ids: string[]) => void;
  discardLocal: (ids: string[]) => void;
  removeByIdsFromDiscardEvent: (ids: string[]) => void;
  removeShiftTopicItems: () => void;
  setHydrated: (hydrated: boolean) => void;
};

type NotificationFeedStore = NotificationFeedState & NotificationFeedActions;

const sortNewestFirst = (items: NotificationItem[]) =>
  [...items].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

const dedupeById = (items: NotificationItem[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
};

const recomputeUnreadCount = (items: NotificationItem[]) =>
  notificationFeedService.getUnreadNonDiscardedCount(items);

export const useNotificationFeedStore = create<NotificationFeedStore>((set) => ({
  items: [],
  unreadCount: 0,
  isHydrated: false,
  meta: null,

  hydrateFromApi: (response) => {
    const items = sortNewestFirst(dedupeById(response.data));
    set({
      items,
      unreadCount: recomputeUnreadCount(items),
      meta: response.meta,
      isHydrated: true,
    });
  },

  prependFromRealtime: (item) => {
    set((state) => {
      const without = state.items.filter((existing) => existing.id !== item.id);
      const items = sortNewestFirst([item, ...without]);
      return {
        ...state,
        items,
        unreadCount: recomputeUnreadCount(items),
      };
    });
  },

  markReadLocal: (ids) => {
    if (!ids.length) {
      return;
    }

    const idSet = new Set(ids);
    const readAt = new Date().toISOString();
    set((state) => {
      const items = state.items.map((item) =>
        idSet.has(item.id) && !item.read_at ? {...item, read_at: readAt} : item,
      );

      return {
        ...state,
        items,
        unreadCount: recomputeUnreadCount(items),
      };
    });
  },

  discardLocal: (ids) => {
    if (!ids.length) {
      return;
    }

    const idSet = new Set(ids);
    const discardedAt = new Date().toISOString();
    set((state) => {
      const items = state.items.map((item) =>
        idSet.has(item.id) && !item.discarded_at ? {...item, discarded_at: discardedAt} : item,
      );

      return {
        ...state,
        items,
        unreadCount: recomputeUnreadCount(items),
      };
    });
  },

  removeByIdsFromDiscardEvent: (ids) => {
    if (!ids.length) {
      return;
    }

    const idSet = new Set(ids);
    set((state) => {
      const items = state.items.filter((item) => !idSet.has(item.id));
      return {
        ...state,
        items,
        unreadCount: recomputeUnreadCount(items),
      };
    });
  },

  removeShiftTopicItems: () => {
    set((state) => {
      const items = state.items.filter((item) => !isShiftNotificationTopic(item.topic));
      return {
        ...state,
        items,
        unreadCount: recomputeUnreadCount(items),
      };
    });
  },

  setHydrated: (isHydrated) => {
    set((state) => ({
      ...state,
      isHydrated,
    }));
  },
}));
